import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { AnnotationPostType, useAnnotationMutations, usePostAnnotations } from '@/hooks/use-annotations';
import { cn } from '@/lib/utils';

interface PostAnnotationsManagerProps {
  postId: string;
  postType: AnnotationPostType;
}

export function PostAnnotationsManager({ postId, postType }: PostAnnotationsManagerProps) {
  const [content, setContent] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');

  const { annotations, isLoading } = usePostAnnotations(postId, postType, { includeSeen: true, enabled: true });
  const { create, update, remove } = useAnnotationMutations(postId, postType);

  const handleCreate = async () => {
    if (!content.trim()) return;
    try {
      await create.mutateAsync({ content: content.trim() });
      setContent('');
    } catch (error) {
      console.error('[PostAnnotationsManager] failed to create annotation', error);
    }
  };

  const handleToggleVisibility = async (annotationId: string, currentValue: boolean) => {
    try {
      await update.mutateAsync({ id: annotationId, isVisible: !currentValue });
    } catch (error) {
      console.error('[PostAnnotationsManager] failed to toggle visibility', error);
    }
  };

  const handleStartEdit = (annotationId: string, existingContent: string) => {
    setEditingId(annotationId);
    setEditingContent(existingContent);
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editingContent.trim()) {
      return;
    }
    try {
      await update.mutateAsync({ id: editingId, content: editingContent.trim() });
      setEditingId(null);
      setEditingContent('');
    } catch (error) {
      console.error('[PostAnnotationsManager] failed to update annotation', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingContent('');
  };

  const handleDelete = async (annotationId: string) => {
    try {
      await remove.mutateAsync(annotationId);
    } catch (error) {
      console.error('[PostAnnotationsManager] failed to delete annotation', error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="annotation-content" className="text-[#D7DADC] font-medium">
          Add a new annotation
        </Label>
        <Textarea
          id="annotation-content"
          value={content}
          onChange={(event) => setContent(event.target.value)}
          placeholder="Share an artist note or behind-the-scenes detail..."
          className="min-h-[120px] resize-none bg-[#0F0F10] border-[#343536] text-[#D7DADC] placeholder:text-gray-400"
          maxLength={1000}
        />
        <div className="flex items-center justify-between text-xs text-[#9CA3AF]">
          <span>{content.length} / 1000</span>
          <Button
            size="sm"
            onClick={handleCreate}
            disabled={!content.trim() || create.isPending}
          >
            {create.isPending ? 'Adding...' : 'Add annotation'}
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        <div className="text-sm text-[#9CA3AF] font-medium uppercase tracking-wide">
          Existing annotations
        </div>
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-20 w-full bg-[#0F0F10]" />
            <Skeleton className="h-20 w-full bg-[#0F0F10]" />
          </div>
        ) : annotations.length === 0 ? (
          <p className="text-sm text-[#9CA3AF]">No annotations added yet. Create your first note above.</p>
        ) : (
          <ul className="space-y-3">
            {annotations.map((annotation) => {
              const isEditing = editingId === annotation.id;
              const relativeCreated = formatDistanceToNow(new Date(annotation.created_at), { addSuffix: true });

              return (
                <li
                  key={annotation.id}
                  className="rounded-lg border border-[#343536] bg-[#1F1F23] p-4 space-y-3"
                >
                  <div className="flex flex-wrap items-center gap-2 text-xs text-[#9CA3AF]">
                    <span>Created {relativeCreated}</span>
                    {annotation.updated_at !== annotation.created_at && (
                      <span className="text-[#71717A]">• Updated {formatDistanceToNow(new Date(annotation.updated_at), { addSuffix: true })}</span>
                    )}
                    {annotation.has_seen && <span className="rounded bg-emerald-500/20 px-2 py-0.5 text-emerald-300">Viewers have seen</span>}
                    {!annotation.is_visible && <span className="rounded bg-amber-500/20 px-2 py-0.5 text-amber-300">Hidden</span>}
                  </div>

                  {isEditing ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editingContent}
                        onChange={(event) => setEditingContent(event.target.value)}
                        maxLength={1000}
                        className="min-h-[100px] resize-none bg-[#0F0F10] border-[#343536] text-[#D7DADC] placeholder:text-gray-400"
                      />
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={handleCancelEdit}>
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSaveEdit}
                          disabled={!editingContent.trim() || update.isPending}
                        >
                          {update.isPending ? 'Saving...' : 'Save'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-[#E5E7EB] whitespace-pre-line">{annotation.content}</p>
                  )}

                  <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#2A2A2D] pt-3">
                    <div className="flex items-center gap-2">
                      <Switch
                        id={`annotation-visible-${annotation.id}`}
                        checked={annotation.is_visible}
                        onCheckedChange={() => handleToggleVisibility(annotation.id, annotation.is_visible)}
                        disabled={update.isPending}
                      />
                      <Label
                        htmlFor={`annotation-visible-${annotation.id}`}
                        className={cn('text-sm', annotation.is_visible ? 'text-[#D7DADC]' : 'text-[#9CA3AF]')}
                      >
                        {annotation.is_visible ? 'Visible to viewers' : 'Hidden from viewers'}
                      </Label>
                    </div>

                    {!isEditing && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStartEdit(annotation.id, annotation.content)}
                          disabled={update.isPending}
                          className="border-[#4B5563] text-[#D7DADC]"
                        >
                          Edit
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(annotation.id)}
                          disabled={remove.isPending}
                        >
                          {remove.isPending ? 'Removing...' : 'Delete'}
                        </Button>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export default PostAnnotationsManager;
